import { useEffect, useMemo, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, PROPERTY_NAMES } from "@/lib/api";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Sun, Loader2, CheckCircle2, Clock } from "lucide-react";
import { BookingSuccessDialog } from "@/components/BookingSuccessDialog";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useT } from "@/lib/i18n";

function addMinutes(hhmm, minutes) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export default function DayUsePage() {
  const { t } = useT();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selection, setSelection] = useState(null); // {room, start, end, price}
  const [pickedStart, setPickedStart] = useState({}); // room_id -> "HH:MM"
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    guests: 1,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);

  const fetchAvailability = async () => {
    if (!selectedDate) return;
    setLoading(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    try {
      const params = { date: dateStr };
      if (propertyFilter !== "all") params.property = propertyFilter;
      const res = await api.get("/availability/dayuse", { params });
      setData(res.data);
      // preselect first available start per room
      const picks = {};
      res.data.rooms.forEach((r) => {
        if (r.available_starts?.length) picks[r.room.id] = r.available_starts[0];
      });
      setPickedStart(picks);
    } catch (e) {
      toast.error("Errore nel caricamento della disponibilità");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line
  }, [selectedDate, propertyFilter]);

  const openBooking = (room, start, duration, price) => {
    const end = addMinutes(start, duration);
    setSelection({ room, start, end, price });
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      guests: 1,
      notes: "",
    });
    setDialogOpen(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!selection) return;
    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.phone.trim()
    ) {
      toast.error("Nome, cognome e telefono sono obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        room_id: selection.room.id,
        booking_type: "dayuse",
        guest_first_name: form.first_name,
        guest_last_name: form.last_name,
        guest_phone: form.phone,
        check_in: format(selectedDate, "yyyy-MM-dd"),
        slot_start: selection.start,
        guests: parseInt(form.guests) || 1,
        notes: form.notes,
      };
      const res = await api.post("/bookings", payload);
      setDialogOpen(false);
      setSuccessBooking(res.data);
      fetchAvailability();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Errore durante la prenotazione",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="dayuse-page" className="min-h-screen bg-background">
      <SiteHeader />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 label-eyebrow mb-3">
          <Sun className="w-3 h-3" /> {t("dayuse.eyebrow")}
        </div>
        <h1 className="font-display font-light text-5xl md:text-6xl text-primary tracking-tight leading-none">
          {t("dayuse.title")}
        </h1>
        <p className="text-foreground/70 mt-5 max-w-2xl text-lg leading-relaxed">
          {t("dayuse.desc.1")}
          <strong>{t("dayuse.desc.std")}</strong>
          {t("dayuse.desc.2")}
          <strong>{t("dayuse.desc.suite")}</strong>. Prenotabile dalle
          <strong> 08:00 alle 02:00</strong> di notte, con 30 minuti di pulizia
          tra un ospite e l&apos;altro.
        </p>

        <div className="grid md:grid-cols-12 gap-8 mt-14">
          <div className="md:col-span-4">
            <div className="card-soft p-6 sticky top-28">
              <div className="label-eyebrow mb-4">{t("common.pick_date")}</div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                disabled={(d) => d < new Date(new Date().toDateString())}
                locale={it}
                className="rounded-md"
                data-testid="dayuse-calendar"
              />
              <div className="mt-6">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t("common.structure")}
                </Label>
                <Select
                  value={propertyFilter}
                  onValueChange={setPropertyFilter}
                >
                  <SelectTrigger
                    className="mt-2"
                    data-testid="dayuse-property-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all_structures")}</SelectItem>
                    <SelectItem value="hotel_due_pini">
                      Hotel Due Pini Conte
                    </SelectItem>
                    <SelectItem value="ga_conte">G&amp;A Conte B&amp;B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-primary">
                {selectedDate
                  ? format(selectedDate, "EEEE d MMMM yyyy", { locale: it })
                  : "Seleziona una data"}
              </h2>
              {loading && (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              )}
            </div>

            <div className="space-y-4">
              {data?.rooms
                ?.filter(
                  (entry) =>
                    !entry.blocked_reason &&
                    entry.available_starts &&
                    entry.available_starts.length > 0,
                )
                .map((entry) => (
                  <RoomCard
                    key={entry.room.id}
                    entry={entry}
                    picked={pickedStart[entry.room.id]}
                    onPick={(t) =>
                      setPickedStart({ ...pickedStart, [entry.room.id]: t })
                    }
                    onBook={() =>
                      openBooking(
                        entry.room,
                        pickedStart[entry.room.id],
                        entry.duration_min,
                        entry.price,
                      )
                    }
                  />
                ))}
              {!loading &&
                (!data?.rooms ||
                  data.rooms.filter(
                    (e) => !e.blocked_reason && e.available_starts?.length > 0,
                  ).length === 0) && (
                  <div className="text-center py-16 text-muted-foreground card-soft">
                    Nessuna camera disponibile in questa data. Prova a scegliere un altro giorno.
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="dayuse-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Conferma prenotazione DayUse
            </DialogTitle>
            <DialogDescription>
              {selection && (
                <>
                  {selection.room.name} · {selection.start} – {selection.end} ·{" "}
                  {selectedDate &&
                    format(selectedDate, "d MMM yyyy", { locale: it })}{" "}
                  · <strong>€{selection.price}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  data-testid="input-first-name"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm({ ...form, first_name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label>Cognome *</Label>
                <Input
                  data-testid="input-last-name"
                  value={form.last_name}
                  onChange={(e) =>
                    setForm({ ...form, last_name: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div>
              <Label>Cellulare *</Label>
              <Input
                data-testid="input-phone"
                type="tel"
                inputMode="tel"
                placeholder="es. 388 092 5320"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Ti contatteremo su questo numero per confermare
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input
                data-testid="input-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="submit-dayuse-booking"
                disabled={submitting}
                className="btn-primary pill w-full"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Conferma prenotazione
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BookingSuccessDialog
        open={!!successBooking}
        booking={successBooking}
        onClose={() => setSuccessBooking(null)}
      />

      <SiteFooter />
      <WhatsAppFloat />
    </div>
  );
}

const RoomCard = ({ entry, picked, onPick, onBook }) => {
  const { room, duration_min, available_starts, blocked_reason, booked_ranges, price } = entry;
  const durationLabel = duration_min === 120 ? "2h" : "3h";
  const disabled = !!blocked_reason || available_starts.length === 0;
  const endTime = useMemo(() => (picked ? addMinutes(picked, duration_min) : ""), [picked, duration_min]);

  const blockedLabel =
    blocked_reason === "closed"
      ? "Chiusa dallo staff"
      : blocked_reason === "overnight"
        ? "Occupata (pernottamento)"
        : available_starts.length === 0
          ? "Nessuna fascia disponibile"
          : null;

  return (
    <div data-testid={`room-card-${room.id}`} className="card-soft p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-xl text-primary">{room.name}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">
            {PROPERTY_NAMES[room.property]} ·{" "}
            {room.type === "suite_vasca" ? "Suite con vasca" : "Standard"} · {durationLabel}
          </div>
        </div>
        <div className="font-display text-2xl text-secondary">€{price}</div>
      </div>

      {disabled ? (
        <div className="py-4 text-sm text-muted-foreground italic">{blockedLabel}</div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-foreground/80">
              <Clock className="w-4 h-4 text-primary" />
              Inizio
            </div>
            <Select value={picked || ""} onValueChange={onPick}>
              <SelectTrigger
                className="w-32"
                data-testid={`start-select-${room.id}`}
              >
                <SelectValue placeholder="Ora" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {available_starts.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`start-option-${room.id}-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              → fine <strong className="text-foreground">{endTime}</strong>
            </span>
            <Button
              onClick={onBook}
              disabled={!picked}
              className="btn-primary pill ml-auto"
              data-testid={`book-dayuse-${room.id}`}
            >
              Prenota
            </Button>
          </div>

          {booked_ranges.length > 0 && (
            <div className="mt-4 text-xs text-muted-foreground">
              <span className="uppercase tracking-widest">Già prenotato:</span>{" "}
              {booked_ranges
                .map((r) => `${r.start}–${r.end}`)
                .join(" · ")}
            </div>
          )}
        </>
      )}
    </div>
  );
};
